import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
    Check,
    Loader2,
    CalendarDays,
    Clock,
    CreditCard,
    PartyPopper,
    ShieldCheck,
    Video,
    ArrowRight,
    ArrowLeft,
    Tag,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    fetchSlots,
    fetchConsultSettings,
    initPayment,
    verifyPayment,
    finalizeBooking,
    validateCoupon,
    apiErr,
} from "@/lib/api";
import { CONSULTATION_PRICE } from "@/lib/config";

const STEPS = ["Date", "Slot", "Payment", "Details", "Done"];

function loadRazorpay() {
    return new Promise((resolve) => {
        if (window.Razorpay) return resolve(true);
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
    });
}

function nextDates(count = 14, weekdays = [0, 1, 2, 3, 4]) {
    const out = [];
    const d = new Date();
    let i = 1;
    while (out.length < count && i < 60) {
        const x = new Date(d);
        x.setDate(d.getDate() + i);
        if (weekdays.includes((x.getDay() + 6) % 7)) out.push(x);
        i++;
    }
    return out;
}

const REVENUE = ["Just starting", "Under ₹1 Lakh", "₹1–5 Lakh", "₹5–20 Lakh", "₹20 Lakh+"];

export default function BookingFlow() {
    const [step, setStep] = useState(0);
    const [date, setDate] = useState(null);
    const [slots, setSlots] = useState([]);
    const [slotId, setSlotId] = useState(null);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [contact, setContact] = useState({ full_name: "", email: "", mobile: "" });
    const [paying, setPaying] = useState(false);
    const [bookingId, setBookingId] = useState(null);
    const [details, setDetails] = useState({
        full_name: "",
        mobile: "",
        email: "",
        business_name: "",
        business_link: "",
        business_category: "",
        current_problem: "",
        monthly_revenue: "",
        support_needed: "",
        expected_result: "",
    });
    const [finalizing, setFinalizing] = useState(false);
    const [couponInput, setCouponInput] = useState("");
    const [coupon, setCoupon] = useState(null);
    const [couponMsg, setCouponMsg] = useState(null);
    const [applyingCoupon, setApplyingCoupon] = useState(false);
    const [settings, setSettings] = useState(null);

    useEffect(() => {
        fetchConsultSettings().then(setSettings).catch(() => {});
    }, []);

    const applyCoupon = async () => {
        if (!couponInput.trim()) return;
        setApplyingCoupon(true);
        try {
            const res = await validateCoupon(couponInput.trim().toUpperCase(), "roadmap", contact.email);
            if (res.valid) { setCoupon(res); setCouponMsg({ ok: true, text: `Coupon applied — ₹${res.discount} off` }); }
            else { setCoupon(null); setCouponMsg({ ok: false, text: res.message || "Invalid coupon" }); }
        } catch { setCouponMsg({ ok: false, text: "Could not validate coupon" }); }
        finally { setApplyingCoupon(false); }
    };

    const dates = useMemo(() => nextDates(14, settings?.weekdays || [0, 1, 2, 3, 4]), [settings]);

    useEffect(() => {
        loadRazorpay();
    }, []);

    const fmt = (d) => d.toISOString().slice(0, 10);
    const slotLabel = slots.find((s) => s.id === slotId)?.label;

    const pickDate = async (d) => {
        const ds = fmt(d);
        setDate(ds);
        setSlotId(null);
        setLoadingSlots(true);
        try {
            const res = await fetchSlots(ds);
            setSlots(res.slots);
            setStep(1);
        } catch {
            toast.error("Could not load slots. Please try again.");
        } finally {
            setLoadingSlots(false);
        }
    };

    const startPayment = async () => {
        if (!contact.full_name || !contact.email || !contact.mobile) {
            toast.error("Please fill your name, email and mobile.");
            return;
        }
        setPaying(true);
        try {
            const ok = await loadRazorpay();
            if (!ok) throw new Error("Razorpay failed to load");
            const order = await initPayment({ date, slot_id: slotId, ...contact, coupon_code: coupon?.code || null });
            setBookingId(order.booking_id);
            setDetails((d) => ({ ...d, ...contact }));

            const rzp = new window.Razorpay({
                key: order.razorpay_key_id,
                amount: order.amount * 100,
                currency: order.currency,
                name: "Abhijit JK",
                description: "90-Min Business Strategy Call",
                order_id: order.razorpay_order_id,
                prefill: {
                    name: contact.full_name,
                    email: contact.email,
                    contact: contact.mobile,
                },
                theme: { color: "#D4AF37" },
                handler: async (resp) => {
                    try {
                        await verifyPayment({
                            booking_id: order.booking_id,
                            razorpay_payment_id: resp.razorpay_payment_id,
                            razorpay_order_id: resp.razorpay_order_id,
                            razorpay_signature: resp.razorpay_signature,
                        });
                        toast.success("Payment successful!");
                        setStep(3);
                    } catch {
                        toast.error("Payment verification failed. Contact support.");
                    } finally {
                        setPaying(false);
                    }
                },
                modal: { ondismiss: () => setPaying(false) },
            });
            rzp.on("payment.failed", () => {
                toast.error("Payment failed. Please try again.");
                setPaying(false);
            });
            rzp.open();
        } catch (e) {
            toast.error(apiErr(e, "Could not start payment. Please try again or reach us on WhatsApp."));
            setPaying(false);
        }
    };

    const submitDetails = async () => {
        const required = [
            "full_name", "mobile", "email", "business_name",
            "business_category", "current_problem", "monthly_revenue",
            "support_needed", "expected_result",
        ];
        if (required.some((k) => !details[k])) {
            toast.error("Please complete all required fields.");
            return;
        }
        setFinalizing(true);
        try {
            await finalizeBooking({ booking_id: bookingId, details });
            setStep(4);
        } catch (e) {
            toast.error(apiErr(e, "Could not confirm booking. Please contact us on WhatsApp."));
        } finally {
            setFinalizing(false);
        }
    };

    const inputCls = "mt-2 h-12 border-white/10 bg-white/5 text-white placeholder:text-white/30 focus-visible:ring-gold-300/40 focus-visible:border-gold-300/50";
    const setD = (k) => (e) => setDetails((d) => ({ ...d, [k]: e.target.value }));

    return (
        <div data-testid="booking-flow">
            {/* STEP INDICATOR */}
            <div className="mb-10 flex items-center justify-between">
                {STEPS.map((s, i) => (
                    <div key={s} className="flex flex-1 items-center">
                        <div
                            className={`flex h-9 w-9 flex-none items-center justify-center rounded-full text-sm font-medium transition ${
                                i < step
                                    ? "bg-gold-300 text-black"
                                    : i === step
                                    ? "glass glow-border gold-text"
                                    : "border border-white/10 text-white/40"
                            }`}
                        >
                            {i < step ? <Check size={15} /> : i + 1}
                        </div>
                        {i < STEPS.length - 1 && (
                            <div className={`mx-2 h-px flex-1 ${i < step ? "bg-gold-300/60" : "bg-white/10"}`} />
                        )}
                    </div>
                ))}
            </div>

            <AnimatePresence mode="wait">
                {/* STEP 1 — DATE */}
                {step === 0 && (
                    <motion.div key="date" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
                        <h3 className="flex items-center gap-2 font-display text-2xl font-medium">
                            <CalendarDays size={20} className="text-gold-300" /> Select a date
                        </h3>
                        <p className="mt-2 text-sm text-white/55">Two sessions available per day.</p>
                        <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                            {dates.map((d) => {
                                const ds = fmt(d);
                                const active = date === ds;
                                return (
                                    <button
                                        key={ds}
                                        data-testid={`date-${ds}`}
                                        onClick={() => pickDate(d)}
                                        className={`rounded-md p-4 text-left transition ${active ? "glass glow-border" : "glass glass-hover"}`}
                                    >
                                        <p className="text-[10px] uppercase tracking-[0.18em] text-white/50">
                                            {d.toLocaleDateString("en-IN", { weekday: "short" })}
                                        </p>
                                        <p className="mt-1 font-display text-xl">
                                            {d.getDate()} {d.toLocaleDateString("en-IN", { month: "short" })}
                                        </p>
                                    </button>
                                );
                            })}
                        </div>
                    </motion.div>
                )}

                {/* STEP 2 — SLOT */}
                {step === 1 && (
                    <motion.div key="slot" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
                        <h3 className="flex items-center gap-2 font-display text-2xl font-medium">
                            <Clock size={20} className="text-gold-300" /> Choose your slot
                        </h3>
                        <p className="mt-2 text-sm text-white/55">{new Date(date).toDateString()}</p>
                        {loadingSlots ? (
                            <div className="mt-8 flex justify-center"><Loader2 className="animate-spin text-gold-300" /></div>
                        ) : (
                            <div className="mt-7 grid gap-4 sm:grid-cols-2">
                                {slots.map((s) => (
                                    <button
                                        key={s.id}
                                        data-testid={`slot-${s.id}`}
                                        disabled={!s.available}
                                        onClick={() => { setSlotId(s.id); setStep(2); }}
                                        className={`rounded-md p-6 text-left transition ${
                                            !s.available ? "cursor-not-allowed border border-white/5 opacity-40" : slotId === s.id ? "glass glow-border" : "glass glass-hover"
                                        }`}
                                    >
                                        <p className="font-display text-xl">{s.label}</p>
                                        <p className="mt-2 text-xs uppercase tracking-[0.16em] text-white/50">
                                            {s.available ? "Available" : "Booked"}
                                        </p>
                                    </button>
                                ))}
                            </div>
                        )}
                        <button onClick={() => setStep(0)} className="mt-8 inline-flex items-center gap-2 text-sm text-white/55 hover:text-white">
                            <ArrowLeft size={14} /> Change date
                        </button>
                    </motion.div>
                )}

                {/* STEP 3 — PAYMENT */}
                {step === 2 && (
                    <motion.div key="pay" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
                        <h3 className="flex items-center gap-2 font-display text-2xl font-medium">
                            <CreditCard size={20} className="text-gold-300" /> Secure payment
                        </h3>
                        <div className="mt-4 glass rounded-md p-5 text-sm text-white/70">
                            <div className="flex flex-wrap items-center gap-4">
                                <span className="inline-flex items-center gap-2"><CalendarDays size={14} className="text-gold-300" /> {new Date(date).toDateString()}</span>
                                <span className="inline-flex items-center gap-2"><Clock size={14} className="text-gold-300" /> {slotLabel}</span>
                                <span className="inline-flex items-center gap-2"><Video size={14} className="text-gold-300" /> Google Meet</span>
                                <span className="ml-auto text-right">
                                    {coupon && <span className="mr-2 text-sm text-white/40 line-through">{CONSULTATION_PRICE}</span>}
                                    <span className="gold-text font-display text-xl font-semibold">₹{(coupon ? coupon.final : (settings?.price || 1999)).toLocaleString("en-IN")}</span>
                                </span>
                            </div>
                            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-white/10 pt-4">
                                <input value={couponInput} onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); setCoupon(null); setCouponMsg(null); }}
                                    placeholder="Coupon code" data-testid="book-coupon-input"
                                    className="flex-1 rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm uppercase tracking-wide text-white placeholder:normal-case placeholder:text-white/30" />
                                <button type="button" onClick={applyCoupon} disabled={applyingCoupon || !couponInput.trim()} data-testid="book-coupon-apply"
                                    className="ghost-button inline-flex items-center gap-2 text-sm disabled:opacity-50">
                                    {applyingCoupon ? <Loader2 size={13} className="animate-spin" /> : <Tag size={13} />} Apply
                                </button>
                                {couponMsg && <p data-testid="book-coupon-msg" className={`w-full text-xs ${couponMsg.ok ? "text-emerald-300" : "text-red-300"}`}>{couponMsg.text}</p>}
                            </div>
                        </div>

                        <div className="mt-6 grid gap-5 sm:grid-cols-2">
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-white/55">Full Name</Label>
                                <Input data-testid="pay-name" value={contact.full_name} onChange={(e) => setContact({ ...contact, full_name: e.target.value })} placeholder="Your name" className={inputCls} />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-white/55">Mobile</Label>
                                <Input data-testid="pay-mobile" value={contact.mobile} onChange={(e) => setContact({ ...contact, mobile: e.target.value })} placeholder="+91…" className={inputCls} />
                            </div>
                            <div className="sm:col-span-2">
                                <Label className="text-xs uppercase tracking-[0.2em] text-white/55">Email</Label>
                                <Input type="email" data-testid="pay-email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="you@business.com" className={inputCls} />
                            </div>
                        </div>

                        <button onClick={startPayment} disabled={paying} data-testid="pay-now" className="gold-button mt-8 inline-flex w-full items-center justify-center gap-2 disabled:opacity-50 sm:w-auto">
                            {paying ? <Loader2 className="animate-spin" size={14} /> : <CreditCard size={14} />}
                            Pay ₹{(coupon ? coupon.final : (settings?.price || 1999)).toLocaleString("en-IN")} Securely
                        </button>
                        <p className="mt-4 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-white/45">
                            <ShieldCheck size={12} /> Razorpay · UPI, Card, Net Banking
                        </p>
                        <button onClick={() => setStep(1)} className="mt-6 inline-flex items-center gap-2 text-sm text-white/55 hover:text-white">
                            <ArrowLeft size={14} /> Back
                        </button>
                    </motion.div>
                )}

                {/* STEP 4 — DETAILS */}
                {step === 3 && (
                    <motion.div key="details" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
                        <h3 className="font-display text-2xl font-medium">Tell me about your business</h3>
                        <p className="mt-2 text-sm text-white/55">This helps me prepare before our session.</p>
                        <div className="mt-7 grid gap-5 sm:grid-cols-2">
                            <div><Label className="text-xs uppercase tracking-[0.2em] text-white/55">Name</Label><Input data-testid="d-name" value={details.full_name} onChange={setD("full_name")} className={inputCls} /></div>
                            <div><Label className="text-xs uppercase tracking-[0.2em] text-white/55">Phone</Label><Input data-testid="d-mobile" value={details.mobile} onChange={setD("mobile")} className={inputCls} /></div>
                            <div><Label className="text-xs uppercase tracking-[0.2em] text-white/55">Email</Label><Input data-testid="d-email" value={details.email} onChange={setD("email")} className={inputCls} /></div>
                            <div><Label className="text-xs uppercase tracking-[0.2em] text-white/55">Business Name</Label><Input data-testid="d-business" value={details.business_name} onChange={setD("business_name")} className={inputCls} /></div>
                            <div><Label className="text-xs uppercase tracking-[0.2em] text-white/55">Website or Instagram</Label><Input data-testid="d-link" value={details.business_link} onChange={setD("business_link")} placeholder="link (optional)" className={inputCls} /></div>
                            <div><Label className="text-xs uppercase tracking-[0.2em] text-white/55">Business Category</Label><Input data-testid="d-category" value={details.business_category} onChange={setD("business_category")} placeholder="e.g. Food, Retail, SaaS" className={inputCls} /></div>
                            <div className="sm:col-span-2">
                                <Label className="text-xs uppercase tracking-[0.2em] text-white/55">Monthly Revenue Range</Label>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {REVENUE.map((r) => (
                                        <button key={r} type="button" data-testid={`d-rev-${r}`} onClick={() => setDetails((d) => ({ ...d, monthly_revenue: r }))} className={`rounded-full px-4 py-2 text-xs transition ${details.monthly_revenue === r ? "bg-gold-300 text-black" : "glass glass-hover text-white/70"}`}>{r}</button>
                                    ))}
                                </div>
                            </div>
                            <div className="sm:col-span-2"><Label className="text-xs uppercase tracking-[0.2em] text-white/55">Current Business Problem</Label><Textarea rows={3} data-testid="d-problem" value={details.current_problem} onChange={setD("current_problem")} className="mt-2 border-white/10 bg-white/5 text-white placeholder:text-white/30" /></div>
                            <div className="sm:col-span-2"><Label className="text-xs uppercase tracking-[0.2em] text-white/55">What support do you need?</Label><Textarea rows={3} data-testid="d-support" value={details.support_needed} onChange={setD("support_needed")} className="mt-2 border-white/10 bg-white/5 text-white placeholder:text-white/30" /></div>
                            <div className="sm:col-span-2"><Label className="text-xs uppercase tracking-[0.2em] text-white/55">What result do you expect?</Label><Textarea rows={3} data-testid="d-result" value={details.expected_result} onChange={setD("expected_result")} className="mt-2 border-white/10 bg-white/5 text-white placeholder:text-white/30" /></div>
                        </div>
                        <button onClick={submitDetails} disabled={finalizing} data-testid="d-submit" className="gold-button mt-8 inline-flex items-center gap-2 disabled:opacity-50">
                            {finalizing ? <Loader2 className="animate-spin" size={14} /> : <ArrowRight size={14} />}
                            Confirm Booking
                        </button>
                    </motion.div>
                )}

                {/* STEP 5 — DONE */}
                {step === 4 && (
                    <motion.div key="done" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="py-8 text-center" data-testid="booking-confirmed">
                        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold-300/15 text-gold-300">
                            <PartyPopper size={28} />
                        </div>
                        <h3 className="mt-6 font-display text-3xl font-medium">Booking Confirmed!</h3>
                        <p className="mx-auto mt-3 max-w-md text-sm text-white/60">
                            Your session is locked for{" "}
                            <span className="text-white">{new Date(date).toDateString()}</span> at{" "}
                            <span className="text-white">{slotLabel}</span>. You&apos;ll receive a
                            confirmation and Google Meet link by email shortly.
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
